import { CardContainer, Icon } from "@screencloud/screencloud-ui-components";
import styled from "styled-components";

/* ---------------------------------- Layout --------------------------------- */

export const StepLayout = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: stretch;
  width: 100%;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`;

export const ChoiceColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xxl, 40px);
  min-width: 0;
  padding: var(--spacing-xxxl, 56px) var(--spacing-xl, 24px) 0;
  background: #f8f8f8;
  overflow-y: auto;
`;

export const ChoiceHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg, 16px);
  width: 100%;
`;

export const ChoiceTitle = styled.h2`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 30px;
  line-height: 45px;
  text-align: center;
  color: var(--color-gray-950, #19191b);
`;

/* Currency selector + billing-cycle toggle (Default view). */
export const SelectorsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 32px;
`;

/* Loading / empty / error message shown in place of the plan cards. */
export const PlanStateMessage = styled.p`
  margin: 0;
  align-self: center;
  max-width: 360px;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: var(--color-gray-400, #7d7e84);
`;

export const CardsRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: var(--spacing-lg, 16px);
  width: 100%;
`;

/* -------------------------------- Plan card -------------------------------- */

export const PlanCardStyled = styled(CardContainer)`
  display: flex;
  flex: 0 0 290.5px;
  flex-direction: column;
  align-items: flex-start;
`;

export const PlanContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xl, 24px);
  width: 100%;
`;

export const PlanTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
`;

export const PlanIcon = styled.img`
  display: block;
  height: 22px;
  width: auto;
  flex-shrink: 0;
`;

export const PlanName = styled.span`
  font-family: Sora, Lato, sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: normal;
  color: var(--color-gray-950, #19191b);
`;

/* Greyed cycle suffix in the Pro-trialist card titles ("Pro Monthly"). */
export const PlanNameSuffix = styled.span`
  font-family: Sora, Lato, sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: var(--color-gray-400, #7d7e84);
`;

export const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
  width: 100%;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

export const Price = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 42px;
  line-height: 56px;
  padding-bottom: 0px;
  color: var(--color-gray-950, #19191b);
`;

export const PriceCaption = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
`;

export const PriceUnit = styled.span`
  color: var(--color-gray-950, #19191b);
`;

export const PriceBilled = styled.span`
  color: #a7a7a7;
`;

export const PlanDescription = styled.p`
  margin: 0;
  width: 100%;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-body-text, #474749);
`;

export const Divider = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0;
  border: 0;
  background: var(--color-gray-100, #e2e3e5);
`;

export const FeatureBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
  width: 100%;
`;

export const FeatureTitle = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-gray-950, #19191b);
`;

export const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 8px);
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-foreground, #404145);
`;

export const CheckIcon = styled(Icon)`
  flex-shrink: 0;
  width: 13px;
  height: 13px;
  background-color: var(--color-gray-950, #19191b);
`;

/* ----------------------------- Exclusive deals ----------------------------- */

export const DealsColumn = styled.div`
  display: flex;
  flex: 0 0 446px;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xxl, 40px);
  width: 446px;
  padding: var(--spacing-xxxl, 56px) var(--spacing-xl, 24px) 0;
  background: #f5f5f5;
  border-left: 1px solid var(--color-gray-100, #e2e3e5);
  overflow-y: auto;
`;

export const DealsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg, 16px);
  width: 288px;
  text-align: center;
`;

export const DealsTitle = styled.h2`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 30px;
  line-height: 45px;
  color: var(--color-gray-950, #19191b);
`;

export const DealsSubtitle = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.25;
  color: var(--color-gray-400, #7d7e84);
`;

export const DealsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg, 16px);
  width: 100%;
`;

export const DealsEmpty = styled.p`
  margin: 0;
  max-width: 288px;
  font-family: Lato, sans-serif;
  font-style: italic;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: var(--color-gray-400, #7d7e84);
`;

export const DealCardStyled = styled(CardContainer)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 297px;
  padding: var(--spacing-xl, 24px) var(--spacing-xxl, 40px);
`;

export const DealTitle = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 20px;
  line-height: 28px;
  color: var(--color-gray-950, #19191b);
`;

export const DealContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl, 24px);
  width: 100%;
`;

export const DealPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

export const DeviceImage = styled.img`
  display: block;
  width: 88px;
  height: auto;
  flex-shrink: 0;
`;

export const DealSavings = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: Lato, sans-serif;
  font-weight: 700;
  color: #a7a7a7;
`;

export const SaveAmount = styled.span`
  font-size: 16px;
  line-height: 24px;
`;

export const DeviceCount = styled.span`
  font-size: 14px;
  line-height: 20px;
`;

export const FootnoteGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs, 4px);
  width: 100%;
`;

export const DealFootnote = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-gray-900, #2f2f32);
`;

export const ShipToRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xxs, 4px);
  width: 100%;
`;

export const ShipToLabel = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-subtle, #7d7e84);
`;

export const GlobeIcon = styled(Icon)`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background-color: var(--color-foreground, #404145);
`;

export const CountrySelect = styled.select`
  flex: 1 0 0;
  min-width: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: Lato, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-foreground, #404145);
`;

export const DiscountValue = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 900;
  line-height: 28px;
  color: var(--color-gray-200, #c5c6ca);
`;

export const DiscountPercent = styled.span`
  font-size: 50px;
`;

export const DiscountWord = styled.span`
  font-size: 20px;
`;

/* ------------------------------ Footer total ------------------------------- */

export const FooterTotal = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--spacing-xxs, 4px);
`;

export const FooterTotalAmount = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 900;
  font-size: 20px;
  line-height: 28px;
  color: var(--color-gray-950, #19191b);
`;

export const FooterTotalPeriod = styled.span`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-gray-400, #7d7e84);
`;
